import React, {useState, useContext} from 'react';
import PropTypes from 'prop-types';
import isEmail from 'validator/lib/isEmail';
import {Anchor, Button, Modal, Dialogue, TextField, Text, addToast} from '@albert-io/atomic';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';

import PeopleContext from '../../people.context';
import {coteachersHelpArticleUrl} from '../coteachers.utils';

const AddCoteacher = () => {
  const [showAddCoteacherModal, setShowAddCoteacherModal] = useState(false);
  const toggleShowCoteacherModal = () => setShowAddCoteacherModal(!showAddCoteacherModal);

  return (
    <>
      <Button onClick={toggleShowCoteacherModal}>Add Co-teacher</Button>
      {showAddCoteacherModal && <AddCoteacherModal handleClose={toggleShowCoteacherModal} />}
    </>
  );
};

export const AddCoteacherModal = ({handleClose}) => {
  const {classroom, queries} = useContext(PeopleContext);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [error, setError] = useState(null);
  async function handleAddCoteacher(e) {
    e.preventDefault();

    const email = e.target.elements.email.value;

    if (!email) {
      setError(`A valid email address is required. Please try again.`);
      return;
    }

    if (!isEmail(email)) {
      setError(`${email} is not a valid email. Please try again.`);
      return;
    }

    // Already a co-teacher? Say so up front instead of round-tripping to the "not approved" error.
    const normalizedEmail = email.trim().toLowerCase();
    const existingCoteachers = queries?.coteachers?.getResource?.();
    const alreadyAdded =
      typeof existingCoteachers?.some === 'function' &&
      existingCoteachers.some(
        (teacher) => (teacher.getEmail?.() || '').toLowerCase() === normalizedEmail
      );
    if (alreadyAdded) {
      setError(`${email} is already a co-teacher on this class.`);
      return;
    }

    try {
      setIsRequestPending(true);
      await genericMandarkRequest('patch', {
        resourcePath: [
          'classrooms_v1',
          classroom.getId(),
          `add_teacher?email=${encodeURIComponent(email.toLowerCase())}`
        ]
      });

      invalidatePartialInterest({
        resourcePath: queries.coteachers.done().resourcePath
      });

      handleClose();

      addToast({
        color: 'positive',
        title: 'Success',
        message: `${email} was added as a co-teacher!`
      });
    } catch (err) {
      // The BE raises UnapprovedCoteacher only when no user matches the email; adding a teacher
      // already on the class fails classroom_teachers uniqueness. Anything else (network, 5xx)
      // gets a generic message rather than asserting a roster state we don't know.
      const errors = err?.response?.body?.errors ?? [];
      if (
        errors.some((e2) => e2.code === 'Errors.BadRequest.ValidationError.UnapprovedCoteacher')
      ) {
        setError(
          `${email} has not been approved. Contact your school admin to approve this user or contact success@albert.io for more help`
        );
      } else if (errors.some((e2) => e2.source?.parameter === 'classroom_teachers')) {
        setError(`${email} is already a co-teacher on this class.`);
      } else {
        setError('Something went wrong adding this co-teacher. Please try again.');
      }
    } finally {
      setIsRequestPending(false);
    }
  }
  return (
    <Modal ariaLabel='Add Co-teacher' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            className={modalContentStyle}
            handleClose={handleClose}
            title='Add Co-teacher'
          >
            <form onSubmit={handleAddCoteacher} noValidate>
              <Dialogue.Body>
                <Text as='p' className='mb-x2'>
                  You must use an email address that has been pre-approved by your school.{' '}
                  <Anchor
                    href={coteachersHelpArticleUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    as='a'
                  >
                    Learn more about co-teachers.
                  </Anchor>
                </Text>
                <TextField
                  border
                  type='email'
                  label='Co-teacher email'
                  placeholder='coteacher@albert.edu'
                  name='email'
                  onChange={() => {
                    if (error) {
                      setError(null);
                    }
                  }}
                  error={!!error}
                  message={error}
                  data-testid='add-coteacher-email'
                />
              </Dialogue.Body>
              <Dialogue.BtnGroup>
                <CloseButtonWrapper>
                  <Button disabled={isRequestPending} type='button' color='secondary'>
                    Cancel
                  </Button>
                </CloseButtonWrapper>
                <Button disabled={isRequestPending} type='submit'>
                  Add
                </Button>
              </Dialogue.BtnGroup>
            </form>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

AddCoteacherModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default AddCoteacher;
